import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, TextField, Button, Link as MuiLink, Alert, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';

const PageTitle = styled(Typography)({
  fontFamily: 'Cormorant Garamond, serif',
  fontSize: '2.5rem',
  color: '#1a1a1a',
  textAlign: 'center',
  marginBottom: '2rem',
});

const StyledForm = styled('form')({
  display: 'flex',
  flexDirection: 'column',
  gap: '1.5rem',
  maxWidth: '400px',
  margin: '0 auto',
});

const StyledTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    borderRadius: 0,
    '& fieldset': {
      borderColor: '#1a1a1a',
    },
    '&:hover fieldset': {
      borderColor: '#c4a661',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#c4a661',
    },
  },
  '& .MuiInputLabel-root': {
    color: '#1a1a1a',
    '&.Mui-focused': {
      color: '#c4a661',
    },
  },
});

const SubmitButton = styled(Button)({
  backgroundColor: '#1a1a1a',
  color: '#fff',
  padding: '1rem',
  '&:hover': {
    backgroundColor: '#c4a661',
  },
  '&.Mui-disabled': {
    backgroundColor: '#ccc',
    color: '#fff',
  },
});

const StyledLink = styled(MuiLink)({
  color: '#1a1a1a',
  textDecoration: 'none',
  '&:hover': {
    color: '#c4a661',
  },
});

const LoadingWrapper = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '1rem',
});

const initialFormState = {
  email: '',
  password: '',
};

// Rate limiting constants
const MAX_LOGIN_ATTEMPTS = 5;
const LOCKOUT_DURATION = 15 * 60 * 1000; // 15 minutes in milliseconds

const Login = () => {
  const [formData, setFormData] = useState(initialFormState);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Rate limiting state
  const [loginAttempts, setLoginAttempts] = useState(() => {
    const stored = localStorage.getItem('loginAttempts');
    return stored ? JSON.parse(stored) : { count: 0, timestamp: null };
  });

  // Get the redirect path from location state, or default to profile
  const from = location.state?.from?.pathname || '/profile';

  // Check and reset lockout on component mount
  useEffect(() => {
    if (loginAttempts.timestamp && Date.now() - loginAttempts.timestamp >= LOCKOUT_DURATION) {
      setLoginAttempts({ count: 0, timestamp: null });
      localStorage.removeItem('loginAttempts');
    }
  }, [loginAttempts.timestamp]);

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate(from, { replace: true });
    }
  }, [user, navigate, from]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value.trim()
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const validateForm = () => {
    // Check if user is locked out
    if (loginAttempts.timestamp && Date.now() - loginAttempts.timestamp < LOCKOUT_DURATION) {
      const minutesLeft = Math.ceil((LOCKOUT_DURATION - (Date.now() - loginAttempts.timestamp)) / 60000);
      setError(`Too many failed attempts. Please try again in ${minutesLeft} minutes.`);
      return false;
    }

    if (!formData.email) {
      setError('Email is required');
      return false;
    }

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }

    if (!formData.password) {
      setError('Password is required');
      return false;
    }

    return true;
  };

  const updateLoginAttempts = (failed = true) => {
    if (!failed) {
      // Reset on successful login
      setLoginAttempts({ count: 0, timestamp: null });
      localStorage.removeItem('loginAttempts');
      return;
    }

    const newCount = loginAttempts.count + 1;
    const newAttempts = {
      count: newCount,
      timestamp: newCount >= MAX_LOGIN_ATTEMPTS ? Date.now() : null
    };
    setLoginAttempts(newAttempts);
    localStorage.setItem('loginAttempts', JSON.stringify(newAttempts));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    if (!validateForm()) return;

    setError('');
    setLoading(true);

    try {
      await login(formData.email, formData.password);
      updateLoginAttempts(false); // Reset attempts on successful login
    } catch (err) {
      let errorMessage = 'Failed to sign in';
      
      // Handle specific Firebase Auth errors
      switch (err.code) {
        case 'auth/user-not-found':
        case 'auth/wrong-password':
          errorMessage = 'Invalid email or password';
          updateLoginAttempts(true);
          if (loginAttempts.count + 1 >= MAX_LOGIN_ATTEMPTS) {
            errorMessage = `Too many failed attempts. Please try again in 15 minutes.`;
          } else {
            errorMessage += `. ${MAX_LOGIN_ATTEMPTS - (loginAttempts.count + 1)} attempts remaining.`;
          }
          break;
        case 'auth/invalid-email':
          errorMessage = 'Invalid email address';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Account temporarily disabled. Please try again later or reset your password.';
          break;
        default:
          errorMessage = err.message;
      }
      
      setError(errorMessage);
      setLoading(false);
    }
  };

  return (
    <Container>
      <Box sx={{ py: 8 }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <PageTitle>Sign In</PageTitle>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          <StyledForm onSubmit={handleSubmit} noValidate>
            <StyledTextField
              label="Email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              fullWidth
              autoComplete="email"
              inputProps={{
                autoCapitalize: 'none'
              }}
              disabled={loading || (loginAttempts.timestamp && Date.now() - loginAttempts.timestamp < LOCKOUT_DURATION)}
              error={error === 'Email is required' || error === 'Invalid email address'}
            />
            <StyledTextField
              label="Password"
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              fullWidth
              autoComplete="current-password"
              disabled={loading || (loginAttempts.timestamp && Date.now() - loginAttempts.timestamp < LOCKOUT_DURATION)}
              error={error === 'Password is required'}
            />
            <SubmitButton 
              type="submit" 
              variant="contained" 
              fullWidth
              disabled={loading || (loginAttempts.timestamp && Date.now() - loginAttempts.timestamp < LOCKOUT_DURATION)}
            >
              {loading ? (
                <LoadingWrapper>
                  <CircularProgress size={20} color="inherit" />
                  <span>Signing in...</span>
                </LoadingWrapper>
              ) : (
                'Sign In'
              )}
            </SubmitButton>
            <Typography textAlign="center" mt={2}>
              Don't have an account?{' '}
              <StyledLink component={Link} to="/register">
                Create one
              </StyledLink>
            </Typography>
          </StyledForm>
        </motion.div>
      </Box>
    </Container>
  );
};

export default Login;
